import { useState, useEffect } from 'react';
import { useRouter } from 'next/router';
import { Alert, Box, Button, Chip, Dialog, Paper, Typography } from '@mui/material';
import { AlertBody, DollarToCoin, MarkdownWrapper } from '@/components/common/index';
import { AddToCartModal, QuantityButtons } from '@/components/Shopping';
import { isBlockchainAsset } from '@/utils/products';
// Hooks
import { useAuth } from '@/hooks/useAuth';
import { useCartContext } from '@/providers/CartProvider';
import { useCurrency } from '@/providers/CurrencyProvider';
import { useIsMobile } from '@/hooks/useIsMobile';
import { useWallet } from '@/hooks/useWallet';
import { routes } from '@/routes/routes';
// Assets
import PlaceholderImage from '@/assets/images/PlaceholderImage';
import { colors } from '@/theme';

const SingleProduct = ({
  description,
  iconImageURL,
  id,
  name,
  price,
  ethPrice,
  isAvailableInApp,
  quantityLimited,
  quantity: productQuantity,
  productTypeId,
}) => {
  const { memberData } = useAuth();
  const { cartError, showAddToModal, handleCloseAddToModal } = useCartContext();
  const { conversionError } = useCurrency();
  const { coreWalletCoinState: hasWallet, walletCoinState } = useWallet();
  const isTablet = useIsMobile(1000);
  const isMobile = useIsMobile(520);
  const [productAdded, setProductAdded] = useState({
    show: false,
    productId: 0,
    quantity: 0,
  });
  const router = useRouter();
  const showModal = !!router.query?.showModal;
  const [quantity, setQuantity] = useState(0);

  const blockChainFlag =
    !hasWallet.hasWallet && !hasWallet.isLoading && isBlockchainAsset(productTypeId);

  useEffect(() => {
    if (showModal) {
      const productData = localStorage.getItem('cartItems');
      const { productId, quantity } = JSON.parse(productData);

      setProductAdded({ show: true, productId, quantity });
      localStorage.removeItem('cartItems');
    }
  }, [showModal]);

  const closeModal = () => {
    setProductAdded({ show: false, productId: 0, quantity: 0 });
    const path = router.asPath.split('?')[0];

    router.replace({ pathname: path, query: { id } });
  };

  const resetQtyCloseModal = () => {
    setQuantity(0);
    handleCloseAddToModal();
    closeModal();
  };
  const isOutOfStock = quantityLimited && productQuantity <= 0;

  const productUnavailable =
    isOutOfStock || (!isAvailableInApp && !memberData?.canPurchaseNodes);
  const quantityLow = quantityLimited && productQuantity <= 10;

  return (
    <Paper
      sx={{
        display: 'flex',
        marginTop: '24px',
        flexDirection: isTablet ? 'column' : 'row',
      }}
    >
      <Box
        sx={{
          height: '300px',
          width: isTablet ? '100%' : '400px',
          margin: isTablet ? '0 auto 24px' : '0 24px 0 0',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        {iconImageURL ? (
          <img
            src={iconImageURL}
            alt={`${name} Image`}
            style={{
              objectFit: 'contain',
              width: '100%',
              height: '100%',
            }}
          />
        ) : (
          <PlaceholderImage width={300} height={300} />
        )}
      </Box>
      <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
        <Box
          data-core-qa="singleProductName"
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '16px',
            width: '100%',
          }}
        >
          <Typography variant="h3">{name}</Typography>
          {productUnavailable ? (
            <Chip
              color="errorLight"
              size="small"
              label={isOutOfStock ? 'Out of Stock' : 'Unavailable'}
            />
          ) : null}
        </Box>
        {cartError.error && (
          <Alert severity="error" variant="outlined">
            <AlertBody header="Cart Error." body={cartError.message} />
          </Alert>
        )}
        {conversionError && (
          <Alert severity="error" variant="outlined">
            <AlertBody header={conversionError.header} body={conversionError.message} />
          </Alert>
        )}
        <DollarToCoin dollar={price} coin={ethPrice} row />
        {quantityLimited ? (
          <Typography
            variant="body1"
            color={quantityLow ? 'error' : colors.warning.main}
            sx={{ marginTop: '8px', fontWeight: 'bold' }}
          >
            {quantityLow
              ? `Only ${productQuantity.toLocaleString('en-US')} left!`
              : `${productQuantity.toLocaleString('en-US')} Available for Purchase`}
          </Typography>
        ) : null}
        <MarkdownWrapper sx={{ marginBottom: '12px' }}>{description}</MarkdownWrapper>
        <Box sx={{ marginTop: 'auto', alignSelf: 'flex-start', marginBottom: '24px' }}>
          {blockChainFlag ? (
            <Button
              variant="contained"
              onClick={() =>
                router.push(
                  !walletCoinState.hasWallet
                    ? routes.setupWallet.path
                    : routes.walletMigration.path
                )
              }
            >
              {!walletCoinState.hasWallet ? 'Setup Wallet' : 'Upgrade Wallet'}
            </Button>
          ) : (
            <QuantityButtons
              buttonText="Add to Cart"
              productId={id}
              quantity={quantity}
              setQuantity={setQuantity}
              disabled={productUnavailable}
              quantityLow={quantityLimited}
              productQuantity={productQuantity}
            />
          )}
        </Box>
      </Box>
      <Dialog open={productAdded.show || showAddToModal} onClose={resetQtyCloseModal}>
        <AddToCartModal
          handleClose
          closeModal={resetQtyCloseModal}
          coinPrice={ethPrice}
          icon={iconImageURL}
          isMobile={isMobile}
          name={name}
          price={price}
          quantity={productAdded.quantity || quantity}
        />
      </Dialog>
    </Paper>
  );
};

export default SingleProduct;
